<script setup lang="ts">
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from "vue";
import { twMerge } from "tailwind-merge";

import { useModalStore } from "@/stores/modals/modals.store";

import CloseButton from "@/components/Reusables/Modal/CloseButton.vue";

import SpecialConditionModal from "@/components/Views/MaterialMapping/Modals/SpecialConditionModal.vue";
import FileModal from "@/components/Views/MaterialMapping/Modals/FileModal.vue";
import MaterialMappingModal from "@/components/Views/MaterialMapping/Modals/MaterialMappingModal/MaterialMappingModal.vue";
import LayerModal from "@/components/Views/MaterialMapping/Library/Modal/LayerModal.vue";
import StagesTableModal from "@/components/Views/MaterialMapping/Library/Modal/StagesTableModal.vue";
import CompareModal from "@/components/Views/MaterialMapping/Library/Modal/CompareModal.vue";
import MaterialMappingExcelUploadModal from "@/components/Views/MaterialMapping/ActionBar/MaterialMappingExcelUploadModal.vue";
import IFCUploadModal from "@/components/Views/MaterialMapping/ActionBar/IFCUploadModal.vue";
import ModalConfirm from "@/components/Views/MaterialMapping/Modals/ModalConfirm.vue";
import CreateMaterialModal from "@/components/Views/MaterialMapping/Modals/CreateMaterialModal/CreateMaterialModal.vue";
import AssignLifeTimeModal from "@/components/Views/MaterialMapping/Modals/AssignLifeTimeModal/AssignLifeTimeModal.vue";
import ProcessingModal from "@/components/Views/MaterialMapping/ActionBar/ProcessingModal.vue";
import ModalMarkdown from "@/components/Reusables/Modal/ModalMarkdown.vue";
import InspectMaterial from "@/components/Views/MaterialMapping/Library/Modal/InspectConstruction/InspectMaterial.vue";
import MaterialServiceLifetimeSelectorModal from "@/components/Views/MaterialMapping/Modals/MaterialServiceLifetimeSelectorModal/MaterialServiceLifetimeSelectorModal.vue";
import WasteSortingExcelUploadModal from "@/pages/WasteSorting/modals/WasteSortingExcelUploadModal.vue";
import WasteMaterialDropModal from "@/pages/WasteSorting/modals/WasteMaterialDropModal.vue";
import WasteMultipleMaterialDropModal from "@/pages/WasteSorting/modals/WasteMultipleMaterialDropModal.vue";
import DiscardModal from "@/views/Library/Modal/DiscardModal.vue";
import AutoMappingLeaveModal from "@/components/Views/MaterialMapping/AutoMapping/AutoMappingLeaveModal.vue";
import WasteSortingEditModal from "@/pages/WasteSorting/modals/WasteSortingEditModal.vue";
import WasteMaterialDetailModal from "@/pages/WasteSorting/modals/WasteMaterialDetailModal.vue";
import WasteSyncAmountsModal from "@/pages/WasteSorting/modals/WasteSyncAmountsModal.vue";
import WasteSyncPickupEditModal from "@/pages/WasteSorting/modals/WasteSyncPickupEditModal.vue";
import WasteMaterialsInfoModal from "@/pages/WasteSorting/modals/WasteMaterialsInfoModal.vue";
import LifetimeTable from "@/components/Views/MaterialMapping/Modals/AssignLifeTimeModal/LifetimeTable/LifetimeTable.vue";
import DeleteModal from "@/components/Reusables/Modal/DeleteModal.vue";
import CustomDataSourcesModal from "@/features/EmissionConsumptionChoice/CustomDataSourcesModal.vue";
import BillingDetailsModal from "@/pages/Purchase/modals/BillingDetailsModal.vue";
import HotspotDeepDiveModal from "@/features/Dashboard/components/HotspotDeepDiveModal.vue";
import StartProjectModal from "@/pages/Projects/components/StartProjectModal.vue";
import ProjectStatusModal from "@/features/Dashboard/components/projectData/ProjectStatusModal.vue";
import RestoreProjectVersionModal from "@/pages/ProjectVersionHistory/modals/RestoreProjectVersionModal.vue";
import CloneProjectStateModal from "@/pages/ProjectVersionHistory/modals/CloneProjectStateModal.vue";
import SaveProjectRevisionModal from "@/pages/ProjectVersionHistory/modals/SaveProjectRevisionModal.vue";
import SaveRevisionFromVersionModal from "@/pages/ProjectVersionHistory/modals/SaveRevisionFromVersionModal.vue";
import MakeRevisionCurrentModal from "@/pages/ProjectVersionHistory/modals/MakeRevisionCurrentModal.vue";
import RenameProjectRevisionModal from "@/pages/ProjectVersionHistory/modals/RenameProjectRevisionModal.vue";
import DeleteProjectRevisionModal from "@/pages/ProjectVersionHistory/modals/DeleteProjectRevisionModal.vue";
import { ReportDisclaimerModal } from "@features/ReportsSelect";
import { SpeckleModal } from "@features/Speckle";

const modalMap = {
  SpecialConditionModal,
  FileModal,
  MaterialMappingModal,
  LayerModal,
  StagesTableModal,
  CompareModal,
  ModalConfirm,
  CreateMaterialModal,
  AssignLifeTimeModal,
  MaterialMappingExcelUploadModal,
  IFCUploadModal,
  ModalMarkdown,
  ProcessingModal,
  InspectMaterial,
  MaterialServiceLifetimeSelectorModal,
  WasteSortingExcelUploadModal,
  WasteMaterialDropModal,
  WasteMultipleMaterialDropModal,
  DiscardModal,
  AutoMappingLeaveModal,
  WasteSortingEditModal,
  WasteMaterialDetailModal,
  WasteSyncAmountsModal,
  WasteSyncPickupEditModal,
  WasteMaterialsInfoModal,
  LifetimeTable,
  DeleteModal,
  StartProjectModal,
  CustomDataSourcesModal,
  BillingDetailsModal,
  HotspotDeepDiveModal,
  ProjectStatusModal,
  RestoreProjectVersionModal,
  CloneProjectStateModal,
  SaveProjectRevisionModal,
  SaveRevisionFromVersionModal,
  MakeRevisionCurrentModal,
  RenameProjectRevisionModal,
  DeleteProjectRevisionModal,
  ReportDisclaimerModal,
  SpeckleModal,
} as const;

const modals = useModalStore();
const stack = computed(() => modals.stack);

const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === "Escape" && stack.value.length > 0) {
    const topModal = stack.value[stack.value.length - 1];
    if (topModal && !topModal.busy) {
      if (topModal.stack) {
        modals.closeTop();
      } else {
        modals.close();
      }
    }
  }
};

const handleBackdropClick = (modal: (typeof stack.value)[number]) => {
  if (modal?.busy) return;
  if (modal?.stack) {
    modals.closeTop();
  } else {
    modals.close();
  }
};

const dialogRefs = ref<Map<string, HTMLElement>>(new Map());

const setDialogRef = (id: string, el: HTMLElement | null) => {
  if (el) dialogRefs.value.set(id, el);
  else dialogRefs.value.delete(id);
};

const getFocusableElements = (container: HTMLElement): HTMLElement[] => {
  return Array.from(
    container.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
  ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
};

const handleFocusTrap = (event: KeyboardEvent, modalId: string) => {
  if (event.key !== "Tab") return;

  const dialog = dialogRefs.value.get(modalId);
  if (!dialog) return;

  const focusable = getFocusableElements(dialog);
  if (focusable.length === 0) return;

  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (event.shiftKey) {
    if (document.activeElement === first) {
      event.preventDefault();
      last.focus();
    }
  } else {
    if (document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
};

const lockBodyScroll = () => {
  document.body.style.overflow = "hidden";
  document.querySelector("#app")?.setAttribute("inert", "");
};

const restoreBodyScroll = () => {
  document.body.style.overflow = "";
  document.querySelector("#app")?.removeAttribute("inert");
};

// Auto-focus the first focusable element when a modal opens; manage scroll lock and inert
watch(
  () => stack.value.length,
  async (newLength, oldLength) => {
    if (newLength > 0 && oldLength === 0) {
      lockBodyScroll();
    } else if (newLength === 0) {
      restoreBodyScroll();
    }

    if (newLength === 0) return;
    await nextTick();
    const topModal = stack.value[stack.value.length - 1];
    const dialog = dialogRefs.value.get(topModal.id);
    if (dialog) {
      const focusable = getFocusableElements(dialog);
      if (focusable.length > 0) focusable[0].focus();
    }
  }
);

onMounted(() => {
  window.addEventListener("keydown", handleEscapeKey);
});

onBeforeUnmount(() => {
  window.removeEventListener("keydown", handleEscapeKey);
  restoreBodyScroll();
});
</script>

<template>
  <Teleport to="body">
    <template
      v-for="(modal, index) in stack"
      :key="modal.id">
      <Transition
        appear
        enter-active-class="duration-fast ease-standard motion-reduce:transition-none"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="duration-fast ease-standard motion-reduce:transition-none"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0">
        <!-- style kept: zIndex is modal token (1000) + stack index for stacked modals -->
        <div
          v-if="modal"
          class="fixed inset-0"
          :style="{ zIndex: `calc(var(--z-index-modal) + ${index})` }">
          <div
            class="absolute inset-0 flex items-center justify-center p-4"
            :class="index === 0 ? 'bg-scrim/60 backdrop-blur-sm' : ''"
            @click.self="handleBackdropClick(modal)">
            <Transition
              appear
              enter-active-class="duration-fast ease-standard motion-reduce:transition-none"
              enter-from-class="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95"
              enter-to-class="opacity-100 translate-y-0 sm:scale-100"
              leave-active-class="duration-fast ease-standard motion-reduce:transition-none"
              leave-from-class="opacity-100 translate-y-0 sm:scale-100"
              leave-to-class="opacity-0 translate-y-2 sm:translate-y-0 sm:scale-95">
              <!-- aria-label falls back to the component name, announced as e.g. "ReportDisclaimerModal";
                   callers pass `ariaLabel` to modals.open to get the visible title announced instead. -->
              <div
                :ref="(el) => setDialogRef(modal.id, el as HTMLElement)"
                role="dialog"
                aria-modal="true"
                :aria-label="modal.ariaLabel || modal.name"
                :class="
                  twMerge(
                    'modal-panel rounded-16 bg-elevated shadow-modal relative flex h-auto max-h-[90dvh] min-h-[460px] w-full max-w-6xl flex-col overflow-hidden ring-1 ring-slate-200',
                    modal.wrapperClass
                  )
                "
                @click.stop
                @keydown="handleFocusTrap($event, modal.id)">
                <CloseButton
                  v-if="!modal.busy"
                  class="absolute right-0 mt-1 mr-1 ml-auto"
                  @click="modal.stack ? modals.closeTop() : modals.close()" />

                <div class="min-h-0 flex-1 overflow-auto px-7 pt-9 pb-6">
                  <component
                    :is="modalMap[modal.name]"
                    v-bind="modal.props"
                    @close="modal.stack ? modals.closeTop($event) : modals.close($event)"
                    @cancel="modal.stack ? modals.closeTop() : modals.close()" />
                </div>
              </div>
            </Transition>
          </div>
        </div>
      </Transition>
    </template>
  </Teleport>
</template>
